import type { AspectRatio } from "@shared/aspectRatios";

// Truest-pixels planning for Instant Ad Generation's multi-ratio flow.
// With a 9:16 base (and neither meme nor compare-models mode), the client
// renders ONE clean 4:5 base creative, mechanically band-extends it into
// the 9:16 deliverable, reuses the 4:5 base directly for a checked 4:5
// tile, and reframes only the remaining ratios generatively — always from
// the clean 4:5, never from a banded canvas.
//
// Invariants pinned by tests:
//   - `generativeDerived` NEVER contains "9:16" (the mechanical extension
//     is the only 9:16 pass — a generative reframe would overwrite it and
//     bill a second render) and NEVER contains "4:5" (the base IS the 4:5).
//   - When the clean path is off, the derived list passes through
//     untouched so non-9:16 base flows stay byte-identical.
export function planInstantAdMultiRatio(opts: {
  effectiveBase: AspectRatio;
  /** Checked ratios minus the base ratio (the caller excludes the base). */
  derivedRatios: AspectRatio[];
  isMeme: boolean;
  compareModels: boolean;
}): {
  useCleanBase45: boolean;
  /** A checked 4:5 tile reuses the clean base directly (no second pass). */
  reuse45Tile: boolean;
  /** Ratios that still need a generative reframe (from the clean 4:5). */
  generativeDerived: AspectRatio[];
} {
  const useCleanBase45 =
    opts.effectiveBase === "9:16" &&
    opts.derivedRatios.length > 0 &&
    !opts.isMeme &&
    !opts.compareModels;
  const generativeDerived = useCleanBase45
    ? // "9:16" can't appear in derivedRatios (it equals the base), but the
      // exclusion is explicit so the mechanical tile can never be
      // overwritten by a generative pass even if the caller changes.
      opts.derivedRatios.filter((r) => r !== "4:5" && r !== "9:16")
    : opts.derivedRatios;
  return {
    useCleanBase45,
    reuse45Tile: useCleanBase45 && opts.derivedRatios.includes("4:5"),
    generativeDerived,
  };
}
